import { isObject, omit } from 'lodash';
import { loadProjectsFeatures } from '@/api/project';
import Vue from 'vue';
import { openJenkins, openFigma, checkGitlabCISwitch, checkCIEnable } from '@/api/jenkins';
import { userHabitRecord, userHabitQuery } from '@/api/user';
import { isOverseasVersion } from 'common-module/src/util';
import {
  updateCurrentProjectSwitchWithVuex,
  updateProjectSwitchWithVuex,
  updateUserSwitchWithVuex
} from '../../stores/updateWithVuexData';

export default {
  namespaced: true,
  state: {
    features: {},
    projectFeatures: {},
    isOpenJenkins: false,
    isOpenFigma: false,
    isOpenFoxLabCI: false,
    isOpenGitlabCI: false
  },
  getters: {
    isSwitch: state => key => !!state.features[key], // 兼容老代码
    getFeature: state => key => state.features[key],
    isProjectSwitch: (state, getters) => (key, projectId) => !!getters.getProjectFeature(key, projectId),
    isCurrentProjectSwitch: (state, getters, rootState) => (key) => !!getters.isProjectSwitch(key, rootState.app.currentProject),
    getProjectFeature: state => (key, projectId) => state.projectFeatures?.[projectId]?.[key],
    getCurrentProjectFeature: (state, getters, rootState) => (key) => getters.getProjectFeature(key, rootState.app.currentProject)
  },
  mutations: {
    cleanFeatures(state){
      state.features = {};
      updateUserSwitchWithVuex();
    },
    changeFeatures(state, payload) {
      if (isObject(payload)) {
        Object.keys(payload).forEach(key=>{
          Vue.set(state.features, key, payload[key]);
        });
        updateUserSwitchWithVuex({
          ...state.features,
          ...payload
        });
      }
    },
    setProjectFeatures(state, features) {
      state.projectFeatures = features || {};
      updateProjectSwitchWithVuex(features);
      const currentProjectId = this.state.app.currentProject;
      if (currentProjectId && features[currentProjectId]) {
        updateCurrentProjectSwitchWithVuex(features[currentProjectId]);
      }
    },
    changeProjectsFeatures(state, data) {
      let features = (data || []).reduce((acc, c) => ({
        ...acc,
        [c.projectId]: omit(c, 'projectId')
      }), {});
      features = { ...state.projectFeatures, ...features };
      state.projectFeatures = features;
      updateProjectSwitchWithVuex(features);
      const currentProjectId = this.state.app.currentProject;
      if (currentProjectId && features[currentProjectId]) {
        updateCurrentProjectSwitchWithVuex(features[currentProjectId]);
      }
    },
    changeCurrentProjectFeatures(state, payload) {
      if (isObject(payload)) {
        let projectId = this.state.app.currentProject;
        if (payload.projectId) {
          projectId = payload.projectId;
          Reflect.deleteProperty(payload, 'projectId');
        }
        if (!projectId) {
          return;
        }

        if (!state.projectFeatures[projectId]) {
          Vue.set(state.projectFeatures, projectId, payload);
          updateCurrentProjectSwitchWithVuex(payload);
          return;
        }
        Object.keys(payload).forEach(key => Vue.set(state.projectFeatures[projectId], key, payload[key]));
        updateCurrentProjectSwitchWithVuex(state.projectFeatures[projectId]);
      }
    },
    setJenkinsOpen(state, isOpen) {
      state.isOpenJenkins = isOpen;
    },
    setFigmaEnabled(state, enabled) {
      state.isOpenFigma = enabled;
    },
    setFoxLabCIEnabled(state, enabled) {
      state.isOpenFoxLabCI = enabled;
    },
    setGitlabCIEnabled(state, enabled) {
      state.isOpenGitlabCI = enabled;
    }
  },
  actions: {
    async toggleFilterABPlan({ state, rootState }) {
      const abSwitch = state.features['user:save_filter_a_b_switch'];
      const to = abSwitch === 'a' ? 'b' : 'a';
      await userHabitRecord({
        linkIdList: [to],
        linkType: 'filterABPlan',
        projectId: 0,
        userId: rootState.user.account?.userId
      });
      state.features['user:save_filter_a_b_switch'] = to;
    },
    async initSetFilterABPlan({ rootState, state }) {
      const res = await userHabitQuery({
        linkType: 'filterABPlan',
        projectId: 0,
        userId: rootState.user.account?.userId
      });
      const abPlan = res?.result?.linkIdList?.[0];
      if (abPlan) {
        state.features['user:save_filter_a_b_switch'] = abPlan;
      }
    },
    init({ commit, dispatch }, payload) {
      commit('cleanFeatures');
      commit('changeFeatures', payload);
      if (!isOverseasVersion) {
        dispatch('initSetFilterABPlan');
      }
    },
    changeFeatures({ commit }, payload) {
      commit('changeFeatures', payload);
    },
    async loadProjectFeatures({ commit }, ids) {
      const result = await loadProjectsFeatures(ids);
      commit('changeProjectsFeatures', result);
    },
    async refreshProjectFeatures({ commit, rootState, dispatch }) {
      commit('setProjectFeatures', {});
      const ids = rootState.app.projectList?.map(p => p.id) || [];
      if (!ids.length) {
        return;
      }
      await dispatch('loadProjectFeatures', ids);
    },
    async refreshJenkinsOpenStatus({ commit }) {
      const isOpen = await openJenkins();
      commit('setJenkinsOpen', isOpen === 1);
    },
    async refreshFigmaOpenStatus({ commit }) {
      const { figmaEnabled = 0 } = await openFigma();
      commit('setFigmaEnabled', !!figmaEnabled);
    },
    async refreshCIStatus({ commit }, payload) {
      const res = await checkCIEnable(payload);
      commit('setFoxLabCIEnabled', res?.foxLab === 1);
      commit('setGitlabCIEnabled', res?.gitLab === 1);
    },
    async refreshFoxLabCIOpenStatus({ commit }, payload) {
      const { platform } = payload || {};
      if (!platform){
        return;
      }
      const res = await checkGitlabCISwitch(platform);
      commit('setFoxLabCIEnabled', res === 1);
    }
  }
};
